import { Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import CusDataGrid from 'components/CusDataGrid';
import CusDrawer from 'components/CusDrawer';
import CusMultipleFilter from 'components/CusMultipleFilter';
import MainCard from 'components/MainCard';
import useMain from 'hooks/useMain';
import React, { useEffect, useState } from 'react';
import { micService } from 'services/mic';
import { getStoredSelectedData, setState } from 'store/reducers/comModule';
import { handleAlertMessage, handleAsyncCall, mapListToOptions } from 'utils/common';
import { sharedMainGridColumns } from './column/table-columns';
import { AdvancedFilter, SidebarForm } from './component';
import { BC_TYPE } from './constant';
import { advancedFilterFields, detailsWorkflowFields } from './field';
import { batchUpdateFilterFields } from './field/form-fields';
import { useData, useFetchData } from './hook/useData';

const ModuleLayoutBatchUpdate = () => {
  const [openSiderbar, setOpenSidebar] = useState(false);
  const { t, useNavigate, useLocation, useSelector, upperCaseLang, dispatch, project } = useMain();
  const [currentStepCode, setCurrentStepCode] = useState('');
  const [currentPortion, setCurrentPortion] = useState('');
  const [currentLabel, setCurrentLabel] = useState('');
  const [disableButton, setDisableButton] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const targetType = Object.values(BC_TYPE).find((type) => {
    return location.pathname.match(type.toLowerCase());
  });

  useFetchData({});

  const {
    categoryOptions,
    subCategoryOptions,
    blockOptions,
    floorOptions,
    unitOptions,
    selectedRegion,
    selectedFloor,
    supplierOptions,
    concreteOptions,
    targetTypeSteps,
    rsCompanyOptions,
    currentBctype,
    setSelectedFloor,
    setSelectedRoom,
    selectedRoom,
    steps,
    setSelectedRegion
  } = useData({
    openSiderbar
  });

  const stepOptions = targetTypeSteps.map(({ Step, Portion, ...rest }) => ({
    label: `${rest[`StepDesc${upperCaseLang}`]} (${Step})`,
    value: Step,
    Portion
  }));

  const selectedRows = useSelector(getStoredSelectedData);

  const handleBatchUpdate = async (data) => {
    const entryData = Object.entries(data);

    const sEntryData = entryData.map(([k, v]) => {
      const value = v === '' ? null : v;
      if (k.match(/remark/gi)) {
        return ['Remark', value];
      }
      if (k.match(/completed/gi)) {
        return ['IsCompleted', value ? 1 : 0];
      }
      return [k, value];
    });

    const sData = Object.fromEntries(sEntryData);

    const rData = await handleAsyncCall({
      service: micService.modifyMultiRfidStepDetails,
      params: {
        StepCode: currentStepCode,
        Id: selectedRows?.map(({ Id }) => Id),
        ...sData
      }
    });

    handleAlertMessage({
      text: t('SUCCESS_SUBMIT'),
      icon: 'success',
      showCancelButton: false,
      cb: () => {
        dispatch(setState(['selectedData', rData]));

        setOpenSidebar(false);
      }
    });
  };

  useEffect(() => {
    if (_.isEmpty(selectedRows)) {
      navigate(-1);
    }
  }, [selectedRows]);

  return (
    <>
      <MainCard content={false} sx={{ mb: '32px' }}>
        <CusMultipleFilter
          handleSubmit={(data) => {
            setCurrentStepCode(data.WorkflowStep);
            setCurrentPortion(stepOptions.find(({ value }) => value === data.WorkflowStep)?.Portion);
            setOpenSidebar(!openSiderbar);
            setCurrentLabel(stepOptions.find(({ value }) => value === data.WorkflowStep)?.label);
          }}
          filters={batchUpdateFilterFields({
            t,
            setDisableButton,
            workflowStepOptions: stepOptions
          })}
          buttonProps={{
            disabled: disableButton
          }}
          title={t('BATCH_UPDATE')}
          buttonLabel={t('BTN_SELECT')}
        />
      </MainCard>
      <MainCard>
        <CusDataGrid
          disableSelectionOnClick
          showExport={false}
          showSearchBar={false}
          isBatchUpdate={true}
          cusToolbarComponents={() => (
            <>
              <Typography color="#bfbfbf" mr="20px">
                {t('SELECTED_ROWS', { SELECTEDROWS: selectedRows.length })}
              </Typography>
            </>
          )}
          getRowId={(row) => row.Id}
          rows={selectedRows}
          columns={sharedMainGridColumns({ currentBctype, theme, t, isBatchUpdate: true, project })}
        />
        <CusDrawer open={openSiderbar} onClose={() => setOpenSidebar(false)}>
          <SidebarForm
            isSelection
            handleSubmit={handleBatchUpdate}
            workflowTitle={t('BATCH_UPDATES_EDIT')}
            title={currentLabel}
            buttonLabel={t('SUBMIT')}
            fields={detailsWorkflowFields({
              t,
              isBatch: true,
              type: targetType,
              stepCode: currentStepCode,
              portion: currentPortion,
              categoryOptions,
              subCategoryOptions,
              supplierOptions,
              blockOptions,
              floorOptions,
              rsCompanyOptions,
              unitOptions,
              concreteOptions,
              setSelectedRegion,
              setSelectedFloor,
              setSelectedRoom,
              selectedRoom,
              selectedRegion,
              selectedFloor,
              upperCaseLang
            })}
            formsLayout={[
              ['Category', 'IsCompleted1'],
              ['ConcreteGrade'],
              ['ManufacturingDate'],
              ['Region'],
              ['Floor'],
              ['RoomId'],
              ['Remark1']
            ]}
          />
        </CusDrawer>
      </MainCard>
    </>
  );
};

export default ModuleLayoutBatchUpdate;
